/* =========================================================================
   CUSTOM FIT \u2014 assets/custom-fit.js
   1. <custom-fit-form>: category-based measurement form, validation,
      surcharge tier + live price, size suggestion, saved measurements.
   2. fetch hook: injects measurements into ANY /cart/add request for a
      custom-size variant (theme or pre-order app) and attaches the
      "Custom Fit Service" line as a nested cart line (parent_line_key).
   3. cart sync: keeps the service line's quantity in step with its garment,
      removes orphans, re-attaches a missing service line.
   ========================================================================= */
(() => {
  if (window.__customFitLoaded) return;
  window.__customFitLoaded = true;

  const G = window.CustomFitGlobal || {};
  const ROOT = G.root || '/';
  let CONFIG = {};
  try {
    CONFIG = JSON.parse(document.getElementById('custom-fit-config').textContent);
  } catch (e) {
    console.warn('[CustomFit] config is not valid JSON', e);
    return;
  }

  const CM = 2.54;
  const SAVED_KEY = 'customFit:saved:v1';
  const FIT_ID = 'Custom Fit ID';
  const SVC_PROP = '_cf_service';
  const registry = new Map(); // custom variant id -> <custom-fit-form>
  const states = (window.__customFitStates = window.__customFitStates || {}); // survives theme re-render
  const origFetch = window.fetch.bind(window);

  /* ---------- helpers ---------- */
  const saved = {
    get() { try { return JSON.parse(localStorage.getItem(SAVED_KEY) || 'null'); } catch (e) { return null; } },
    set(v) { try { localStorage.setItem(SAVED_KEY, JSON.stringify(v)); } catch (e) { /* ignore */ } }
  };
  const money = (cents) => {
    try {
      return new Intl.NumberFormat(G.locale || 'en-IN', { style: 'currency', currency: G.currency || 'INR', maximumFractionDigits: 0 }).format(cents / 100);
    } catch (e) { return String(Math.round(cents / 100)); }
  };
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const roundTo = (n, step) => Math.round(n / step) * step;
  const newFitId = () => 'CF-' + Date.now().toString(36).slice(-3).toUpperCase() + Math.random().toString(36).slice(2, 5).toUpperCase();
  // Same ref survives page reloads for 7 days (per product), so a WhatsApp chat
  // started before ordering carries the same Custom Fit ID as the order line.
  const storedFitId = (productId, renew) => {
    const k = 'customFit:id:' + productId;
    try {
      const o = JSON.parse(localStorage.getItem(k) || 'null');
      if (!renew && o && o.id && Date.now() - o.t < 7 * 864e5) return o.id;
      const id = newFitId();
      localStorage.setItem(k, JSON.stringify({ id, t: Date.now() }));
      return id;
    } catch (e) { return newFitId(); }
  };
  const ftIn = (inches) => `${Math.floor(inches / 12)}'${inches % 12}"`;
  const refreshCart = () => {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  };
  const postJSON = (path, body) => origFetch(ROOT + path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });

  /* =========================================================================
     1. MEASUREMENT FORM
     ========================================================================= */
  class CustomFitForm extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      try { this.product = JSON.parse(this.querySelector('[data-cf-product]').textContent); }
      catch (e) { console.warn('[CustomFit] product data missing', e); return; }
      this._init = true;

      // Phase 1: "use_profiles": false -> everyone gets the "default" profile (bust, waist, hip)
      this.profile = (CONFIG.use_profiles && CONFIG.profiles[this.product.profile]) || CONFIG.profiles.default;
      this.form = this.findForm();
      if (!this.form) { console.warn('[CustomFit] product form not found'); return; }
      this.scope = this.findScope();

      const key = this.product.id + ':' + this.product.profile;
      this.state = states[key] = states[key] || { unit: 'in', values: {}, height: '', notes: '', confirmed: false, fitId: storedFitId(this.product.id), touched: {} };
      this.showAll = false;

      this.render();
      this.bind();
      this.syncVariant();
      this.update();
    }

    disconnectedCallback() {
      document.removeEventListener('change', this._onDocChange);
      document.removeEventListener('variant:change', this._onDocChange);
      document.removeEventListener('click', this._onClick, true);
      this.product?.customVariantIds.forEach((id) => registry.get(String(id)) === this && registry.delete(String(id)));
    }

    findForm() {
      let n = this.parentElement;
      while (n && n !== document.documentElement) {
        const f = n.querySelector('form[action*="/cart/add"][id]');
        if (f) return f;
        n = n.parentElement;
      }
      return null;
    }

    findScope() {
      let n = this.parentElement;
      while (n && !n.contains(this.form)) n = n.parentElement;
      return n || document;
    }

    /* ---------- render ---------- */
    fieldList() {
      return this.profile.fields.map(([key, req, labelOverride]) => {
        const def = CONFIG.fields[key];
        return def ? { ...def, key, required: !!req, label: labelOverride || def.label } : null;
      }).filter(Boolean);
    }

    render() {
      const c = CONFIG.copy;
      const h = CONFIG.height;
      const fields = this.fieldList();
      this.fields = fields;
      const pid = this.product.id;

      const fieldHtml = fields.map((f) => `
        <div class="cf-field" data-field="${f.key}">
          <label for="cf-${f.key}-${pid}">${esc(f.label)}${f.required ? ' <span class="cf-req">*</span>' : ''}</label>
          <div class="cf-field__control">
            <input id="cf-${f.key}-${pid}" type="number" inputmode="decimal" data-key="${f.key}" autocomplete="off">
            <span class="cf-field__unit" data-unit-label></span>
          </div>
          <p class="cf-field__msg" data-msg></p>
        </div>`).join('');

      let heightHtml = '';
      if (this.profile.height !== undefined) {
        const opts = [];
        for (let i = h.min; i <= h.max; i++) opts.push(`<option value="${i}">${ftIn(i)} (${Math.round(i * CM)} cm)</option>`);
        heightHtml = `
          <div class="cf-field" data-field="height">
            <label for="cf-height-${pid}">${esc(h.label)}${this.profile.height ? ' <span class="cf-req">*</span>' : ''}</label>
            <div class="cf-field__control">
              <select id="cf-height-${pid}" data-key="height"><option value="">Select</option>${opts.join('')}</select>
            </div>
            <p class="cf-field__msg" data-msg></p>
          </div>`;
      }

      const helpItems = fields.map((f) => `<li><strong>${esc(f.label)}:</strong> ${esc(f.help)}</li>`).join('')
        + (heightHtml ? `<li><strong>${esc(h.label)}:</strong> ${esc(h.help)}</li>` : '');

      this.innerHTML = `
        <div class="cf__head">
          <p class="cf__title">${esc(c.title)} <span class="cf__profile">${esc(this.profile.label)}</span></p>
          <p class="cf__sub">${esc(c.subtitle)}</p>
        </div>
        <div class="cf__toolbar">
          <div class="cf__unit" role="group" aria-label="Measurement unit">
            <button type="button" data-unit="in">Inches</button><button type="button" data-unit="cm">Cm</button>
          </div>
          <button type="button" class="cf__link" data-use-saved hidden>${esc(c.saved)}</button>
        </div>
        <div class="cf__grid">${fieldHtml}${heightHtml}</div>
        <details class="cf__help"><summary>How to measure</summary><ul>${helpItems}</ul></details>
        <div class="cf__alerts" data-alerts aria-live="polite"></div>
        <div class="cf__suggest" data-suggest hidden></div>
        <div class="cf__price" data-price></div>
        <div class="cf-field cf-field--full">
          <label for="cf-notes-${pid}">Fit notes (optional)</label>
          <textarea id="cf-notes-${pid}" data-key="notes" maxlength="250" placeholder="e.g. prefer a relaxed fit at the waist"></textarea>
        </div>
        <label class="cf__confirm"><input type="checkbox" data-key="confirmed"><span>${esc(c.confirm)}</span></label>
        <p class="cf__msg-confirm" data-confirm-msg></p>
        <p class="cf__lead">${esc(c.lead_time)}</p>
        ${this.contactHtml()}
        <div data-hidden-props hidden></div>`;

      // restore state
      this.querySelectorAll('input[type="number"][data-key]').forEach((el) => { el.value = this.state.values[el.dataset.key] ?? ''; });
      const hs = this.querySelector('select[data-key="height"]'); if (hs) hs.value = this.state.height;
      this.querySelector('[data-key="notes"]').value = this.state.notes;
      this.querySelector('[data-key="confirmed"]').checked = this.state.confirmed;
      this.querySelector('[data-use-saved]').hidden = !saved.get();
      this.applyUnit(false);
    }

    contactHtml() {
      const ct = CONFIG.contact || {};
      const msg = (ct.message || '')
        .replace('{product}', this.product.title)
        .replace('{url}', location.origin + location.pathname)
        .replace('{ref}', this.state.fitId);
      const links = [];
      const wa = String(ct.whatsapp || '').replace(/\D/g, '');
      if (wa) links.push(`<a class="cf__link" href="https://wa.me/${wa}?text=${encodeURIComponent(msg)}" target="_blank" rel="noopener">WhatsApp us</a>`);
      if (ct.email) links.push(`<a class="cf__link" href="mailto:${esc(ct.email)}?subject=${encodeURIComponent('Custom size: ' + this.product.title)}&body=${encodeURIComponent(msg)}">email us</a>`);
      if (!links.length) return '';
      return `<p class="cf__contact">${esc(ct.text || '')} ${links.join(' or ')}.</p>`;
    }

    /* ---------- events ---------- */
    bind() {
      this.addEventListener('input', (e) => this.onField(e));
      this.addEventListener('change', (e) => this.onField(e));
      this.addEventListener('focusout', (e) => { const k = e.target.dataset?.key; if (k) { this.state.touched[k] = true; this.update(); } });
      this.addEventListener('click', (e) => {
        const u = e.target.closest('[data-unit]');
        if (u) { this.setUnit(u.dataset.unit); return; }
        if (e.target.closest('[data-use-saved]')) { this.useSaved(); return; }
        const sw = e.target.closest('[data-switch-unit]');
        if (sw) { this.state.unit = sw.dataset.switchUnit; this.applyUnit(false); this.update(); return; }
        const pick = e.target.closest('[data-pick-size]');
        if (pick) this.pickStandardSize(pick.dataset.pickSize);
      });

      this._onDocChange = () => setTimeout(() => this.syncVariant(), 60);
      document.addEventListener('change', this._onDocChange);
      document.addEventListener('variant:change', this._onDocChange);

      this._onClick = (e) => this.guard(e);
      document.addEventListener('click', this._onClick, true);
    }

    onField(e) {
      const k = e.target.dataset?.key;
      if (!k) return;
      if (k === 'notes') this.state.notes = e.target.value;
      else if (k === 'confirmed') this.state.confirmed = e.target.checked;
      else if (k === 'height') { this.state.height = e.target.value; this.state.touched.height = true; }
      else this.state.values[k] = e.target.value;
      this.update();
    }

    setUnit(unit) {
      if (unit === this.state.unit) return;
      const toCm = unit === 'cm';
      Object.keys(this.state.values).forEach((k) => {
        const v = parseFloat(this.state.values[k]);
        if (!isFinite(v)) return;
        this.state.values[k] = String(toCm ? roundTo(v * CM, 0.5) : roundTo(v / CM, 0.25));
      });
      this.state.unit = unit;
      this.querySelectorAll('input[type="number"][data-key]').forEach((el) => { el.value = this.state.values[el.dataset.key] ?? ''; });
      this.applyUnit();
    }

    applyUnit(doUpdate = true) {
      const cm = this.state.unit === 'cm';
      this.querySelectorAll('[data-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === this.state.unit)));
      this.querySelectorAll('[data-unit-label]').forEach((s) => { s.textContent = cm ? 'cm' : 'in'; });
      this.fields.forEach((f) => {
        const el = this.querySelector(`input[data-key="${f.key}"]`);
        const min = cm ? Math.floor(f.min * CM) : f.min;
        const max = cm ? Math.ceil(f.max * CM) : f.max;
        el.min = min; el.max = max; el.step = cm ? '0.5' : '0.25';
        el.placeholder = `${min}\u2013${max}`;
      });
      if (doUpdate) this.update();
    }

    useSaved() {
      const s = saved.get(); if (!s) return;
      const cm = this.state.unit === 'cm';
      Object.entries(s.values || {}).forEach(([k, inches]) => {
        if (!this.fields.some((f) => f.key === k)) return;
        this.state.values[k] = String(cm ? roundTo(inches * CM, 0.5) : inches);
      });
      if (s.height) this.state.height = String(s.height);
      this.render(); // listeners live on the host element, so no re-bind needed
      this.update();
    }

    /* ---------- variant ---------- */
    currentVariantId() {
      const input = this.form.querySelector('[name="id"]') || document.querySelector(`[name="id"][form="${this.form.id}"]`);
      return String((input && input.value) || new URL(location.href).searchParams.get('variant') || '');
    }

    syncVariant() {
      const vid = this.currentVariantId();
      const active = this.product.customVariantIds.map(String).includes(vid);
      this.hidden = !active;
      this.product.customVariantIds.forEach((id) => {
        if (active) registry.set(String(id), this); else if (registry.get(String(id)) === this) registry.delete(String(id));
      });
      // Buy it now skips our surcharge line, so hide it for custom sizes
      this.scope.querySelectorAll('.shopify-payment-button').forEach((el) => { el.style.display = active ? 'none' : ''; });
      this.update();
    }

    /* ---------- validation ---------- */
    compute() {
      const cm = this.state.unit === 'cm';
      const errors = {}; const alerts = []; const inches = {};

      this.fields.forEach((f) => {
        const raw = String(this.state.values[f.key] ?? '').trim();
        if (raw === '') { if (f.required) errors[f.key] = 'Required'; return; }
        const v = parseFloat(raw);
        if (!isFinite(v) || v <= 0) { errors[f.key] = 'Enter a valid number'; return; }
        const inch = cm ? v / CM : v;
        if (inch < f.min || inch > f.max) {
          const unit = cm ? 'cm' : 'in';
          const lo = cm ? Math.floor(f.min * CM) : f.min; const hi = cm ? Math.ceil(f.max * CM) : f.max;
          const alt = cm ? v : v / CM; // value read in the other unit, in inches
          const looksOther = cm ? (v >= f.min && v <= f.max) : (alt >= f.min && alt <= f.max);
          errors[f.key] = `Should be ${lo}\u2013${hi} ${unit}` + (looksOther
            ? ` \u00b7 <button type="button" class="cf__link" data-switch-unit="${cm ? 'in' : 'cm'}">Looks like ${cm ? 'inches' : 'cm'} \u2014 switch?</button>` : '');
          return;
        }
        inches[f.key] = inch;
      });

      if (this.profile.height !== undefined) {
        if (this.state.height) inches.height = parseInt(this.state.height, 10);
        else if (this.profile.height) errors.height = 'Required';
      }

      (CONFIG.rules || []).forEach((r) => {
        const a = inches[r.a]; const b = inches[r.b];
        if (a === undefined || b === undefined) return;
        let bad = false;
        if (r.type === 'lt') bad = !(a < b);
        if (r.type === 'ratio') bad = a / b < r.min || a / b > r.max;
        if (r.type === 'diff_max') bad = a - b > r.max;
        if (r.type === 'abs_diff_max') bad = Math.abs(a - b) > r.max;
        if (bad) alerts.push({ level: r.level, msg: r.msg, keys: [r.a, r.b] });
      });

      // surcharge tier
      let tier = 0; const reasons = [];
      Object.entries(CONFIG.surcharge?.thresholds || {}).forEach(([k, ths]) => {
        const v = inches[k]; if (v === undefined) return;
        ths.forEach((t, i) => {
          if (v > t && i + 1 > tier) tier = i + 1;
          if (v > t && i === 0) reasons.push(`${k === 'height' ? 'Height' : (this.fields.find((f) => f.key === k)?.label || k)} above ${k === 'height' ? ftIn(t) : t + ' in'}`);
        });
      });
      const tierDef = (CONFIG.surcharge?.tiers || [])[tier] || { label: 'Standard', variant_title: 'Standard' };
      const service = (G.serviceVariants || {})[tierDef.variant_title] || null;

      const hardErrors = Object.keys(errors).length + alerts.filter((a) => a.level === 'error').length;
      return { errors, alerts, inches, tier, tierDef, reasons, service, valid: hardErrors === 0 };
    }

    update() {
      if (!this.fields) return;
      const r = (this.result = this.compute());

      // field messages
      this.querySelectorAll('.cf-field[data-field]').forEach((wrap) => {
        const k = wrap.dataset.field;
        const show = this.showAll || this.state.touched[k];
        const msg = show ? r.errors[k] : '';
        const flagged = r.alerts.some((a) => a.keys.includes(k));
        wrap.classList.toggle('is-invalid', !!msg);
        wrap.classList.toggle('is-flagged', !msg && flagged);
        wrap.querySelector('[data-msg]').innerHTML = msg || '';
      });

      // cross-field alerts
      this.querySelector('[data-alerts]').innerHTML = r.alerts
        .map((a) => `<p class="cf__alert cf__alert--${a.level}">${esc(a.msg)}</p>`).join('');

      // price
      const base = this.product.variants[this.currentVariantId()]?.price || 0;
      const extra = r.service?.price || 0;
      this.querySelector('[data-price]').innerHTML = extra > 0
        ? `<p><strong>${esc(r.tierDef.label)}: +${money(extra)}</strong>${r.reasons.length ? ` <span class="cf__muted">(${esc(r.reasons.join(', '))})</span>` : ''}</p>
           <p class="cf__total">Total: ${money(base + extra)}</p>`
        : (Object.keys(r.inches).length ? `<p class="cf__muted">${esc(CONFIG.copy.no_surcharge)}</p>` : '');

      // standard size suggestion
      const sug = this.querySelector('[data-suggest]');
      const size = this.matchStandardSize(r.inches);
      if (size && r.tier === 0) {
        const label = size.toUpperCase();
        sug.innerHTML = `<p>${esc(CONFIG.copy.suggest.replace('{size}', label))}</p>
          <button type="button" class="cf__btn" data-pick-size="${esc(size)}">${esc(CONFIG.copy.suggest_cta.replace('{size}', label))}</button>`;
        sug.hidden = false;
      } else sug.hidden = true;

      const cmsg = this.querySelector('[data-confirm-msg]');
      cmsg.textContent = this.showAll && !this.state.confirmed ? 'Please confirm your measurements.' : '';

      this.writeHiddenProps();
    }

    matchStandardSize(inches) {
      const sc = CONFIG.size_chart; if (!sc) return null;
      const keys = (sc.match_fields || []).filter((k) => this.fields.some((f) => f.key === k));
      if (!keys.length || keys.some((k) => inches[k] === undefined)) return null;
      for (const [size, ranges] of Object.entries(sc.sizes || {})) {
        if (!keys.every((k) => ranges[k] && inches[k] >= ranges[k][0] && inches[k] <= ranges[k][1])) continue;
        const variant = Object.values(this.product.variants).find((v) => v.available && v.options.some((o) => String(o).toLowerCase() === size));
        if (variant) return size;
      }
      return null;
    }

    pickStandardSize(size) {
      const input = Array.from(this.scope.querySelectorAll('input[type="radio"]'))
        .find((i) => String(i.value).toLowerCase() === size);
      if (input) { input.click(); input.dispatchEvent(new Event('change', { bubbles: true })); return; }
      const select = Array.from(this.scope.querySelectorAll('select')).find((s) => Array.from(s.options).some((o) => o.value.toLowerCase() === size));
      if (select) { select.value = Array.from(select.options).find((o) => o.value.toLowerCase() === size).value; select.dispatchEvent(new Event('change', { bubbles: true })); }
    }

    /* ---------- properties sent to the cart ---------- */
    properties() {
      const r = this.result || this.compute();
      const unit = this.state.unit === 'cm' ? 'cm' : 'in';
      const p = { [FIT_ID]: this.state.fitId, 'Fit Profile': this.profile.label };
      this.fields.forEach((f) => {
        const raw = String(this.state.values[f.key] ?? '').trim();
        if (raw !== '') p[f.label] = `${parseFloat(raw)} ${unit}`;
      });
      if (r.inches.height) p.Height = `${ftIn(r.inches.height)} (${Math.round(r.inches.height * CM)} cm)`;
      if (this.state.notes.trim()) p['Fit Notes'] = this.state.notes.trim();
      p['Custom Fit Tier'] = r.tierDef.label;
      p['Measurements Confirmed'] = 'Yes';
      if (r.service) p[SVC_PROP] = String(r.service.id);
      return p;
    }

    serviceProperties() {
      const r = this.result || this.compute();
      const p = { [FIT_ID]: this.state.fitId, For: this.product.title, Tier: r.tierDef.label };
      if (r.reasons.length) p.Reason = r.reasons.join(', ');
      return p;
    }

    writeHiddenProps() {
      const wrap = this.querySelector('[data-hidden-props]');
      if (!wrap) return;
      wrap.innerHTML = '';
      if (this.hidden) return;
      Object.entries(this.properties()).forEach(([k, v]) => {
        const i = document.createElement('input');
        i.type = 'hidden'; i.name = `properties[${k}]`; i.value = v; i.setAttribute('form', this.form.id);
        wrap.appendChild(i);
      });
    }

    isReady() { return !this.hidden && this.result?.valid && this.state.confirmed; }

    /* ---------- block add-to-cart until valid ---------- */
    guard(e) {
      if (this.hidden) return;
      const btn = e.target.closest('button, input[type="submit"], [role="button"]');
      if (!btn || this.contains(btn)) return;
      const belongs = btn.form === this.form || this.form.contains(btn) || btn.getAttribute('form') === this.form.id;
      if (!belongs) return;
      const label = `${btn.textContent || ''} ${btn.value || ''} ${btn.className || ''}`;
      if (!(btn.type === 'submit' || /pre-?order|add to (cart|bag)|buy/i.test(label))) return;

      this.update();
      if (this.isReady()) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      this.showAll = true;
      this.update();
      const first = this.querySelector('.cf-field.is-invalid input, .cf-field.is-invalid select, .cf__alert--error')
        || (!this.state.confirmed && this.querySelector('[data-key="confirmed"]')) || this;
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (first.focus) setTimeout(() => first.focus({ preventScroll: true }), 300);
    }

    onAdded() {
      saved.set({ values: this.result.inches, height: this.result.inches.height || '' });
      this.state.fitId = storedFitId(this.product.id, true); // next add = separate line, new ref
      this.querySelector('[data-use-saved]').hidden = false;
      this.writeHiddenProps();
    }
  }
  customElements.define('custom-fit-form', CustomFitForm);

  /* =========================================================================
     2. FETCH HOOK \u2014 catches theme and app add-to-cart calls
     ========================================================================= */
  const ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  const MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function readAdd(init) {
    const body = init && init.body;
    if (!body) return null;
    if (body instanceof FormData || body instanceof URLSearchParams) {
      return { kind: 'form', id: body.get('id'), qty: parseInt(body.get('quantity') || '1', 10) };
    }
    if (typeof body === 'string') {
      try {
        const o = JSON.parse(body);
        const item = o.items ? o.items[0] : o;
        return { kind: 'json', obj: o, id: item && item.id, qty: parseInt((item && item.quantity) || 1, 10) };
      } catch (e) {
        const sp = new URLSearchParams(body);
        return { kind: 'urlstring', sp, id: sp.get('id'), qty: parseInt(sp.get('quantity') || '1', 10) };
      }
    }
    return null;
  }

  function inject(init, ctx, props) {
    if (ctx.kind === 'form') { Object.entries(props).forEach(([k, v]) => init.body.set(`properties[${k}]`, v)); return init; }
    if (ctx.kind === 'urlstring') { Object.entries(props).forEach(([k, v]) => ctx.sp.set(`properties[${k}]`, v)); return { ...init, body: ctx.sp.toString() }; }
    const item = ctx.obj.items ? ctx.obj.items[0] : ctx.obj;
    item.properties = { ...(item.properties || {}), ...props };
    return { ...init, body: JSON.stringify(ctx.obj) };
  }

  async function addServiceLine(svcId, qty, parentKey, props) {
    const item = { id: Number(svcId), quantity: qty, properties: props };
    let res = await postJSON('cart/add.js', { items: [{ ...item, parent_line_key: parentKey }] });
    if (!res.ok) res = await postJSON('cart/add.js', { items: [item] }); // fallback: un-nested
    return res.ok;
  }

  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    if (!ADD_RE.test(url)) {
      const res = await origFetch(input, init);
      if (MUTATION_RE.test(url)) scheduleSync();
      return res;
    }
    const ctx = readAdd(init);
    const el = ctx && registry.get(String(ctx.id));
    if (!el || !el.isReady()) {
      const res = await origFetch(input, init);
      scheduleSync();
      return res;
    }

    const props = el.properties();
    const svcProps = el.serviceProperties();
    const res = await origFetch(input, inject(init, ctx, props));
    if (res.ok) {
      try {
        const data = await res.clone().json();
        const line = data.items ? data.items.find((i) => i.properties && i.properties[FIT_ID] === props[FIT_ID]) : data;
        if (props[SVC_PROP] && line && line.key) await addServiceLine(props[SVC_PROP], line.quantity || ctx.qty, line.key, svcProps);
        el.onAdded();
      } catch (e) { console.warn('[CustomFit] service line add failed', e); }
      scheduleSync(true);
    }
    return res;
  };

  /* =========================================================================
     3. CART SYNC
     ========================================================================= */
  let syncTimer = null; let syncing = false;
  function scheduleSync(forceRefresh) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => syncCart(forceRefresh), 350);
  }

  // Copy each custom piece's measurements into the order's note attributes
  // ("Additional details"), because Shopify's Orders CSV export includes the
  // "Note Attributes" column but not line-item properties.
  const ATTR_PREFIX = 'Custom Fit ';
  const SKIP_PROPS = [FIT_ID, 'Fit Profile', 'Measurements Confirmed'];
  async function syncNoteAttributes(cart, parents) {
    const want = {};
    for (const [id, p] of parents) {
      const parts = [p.product_title + (p.variant_title ? ' (' + p.variant_title + ')' : '')];
      Object.entries(p.properties || {}).forEach(([k, v]) => {
        if (k.charAt(0) === '_' || SKIP_PROPS.includes(k) || v === '' || v == null) return;
        parts.push(k + ': ' + String(v).replace(/\s+/g, ' ').trim());
      });
      if (p.quantity > 1) parts.push('Qty: ' + p.quantity);
      want[ATTR_PREFIX + id] = parts.join(' | ');
    }
    const have = cart.attributes || {};
    const diff = {};
    Object.keys(have).forEach((k) => { if (k.indexOf(ATTR_PREFIX + 'CF-') === 0 && !(k in want)) diff[k] = ''; });
    Object.entries(want).forEach(([k, v]) => { if (have[k] !== v) diff[k] = v; });
    if (Object.keys(diff).length) await postJSON('cart/update.js', { attributes: diff });
  }

  async function syncCart(forceRefresh) {
    if (syncing) return;
    syncing = true; let changed = false;
    try {
      const cart = await (await origFetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      const parents = new Map(); const children = [];
      cart.items.forEach((item) => {
        const id = item.properties && item.properties[FIT_ID];
        if (!id) return;
        if (item.handle === G.serviceHandle) children.push(item); else parents.set(id, item);
      });
      for (const c of children) {
        const p = parents.get(c.properties[FIT_ID]);
        const want = p ? p.quantity : 0;
        if (c.quantity !== want) { await postJSON('cart/change.js', { id: c.key, quantity: want }); changed = true; }
      }
      for (const [id, p] of parents) {
        if (children.some((c) => c.properties[FIT_ID] === id)) continue;
        const svc = p.properties[SVC_PROP];
        if (!svc) continue;
        if (await addServiceLine(svc, p.quantity, p.key, { [FIT_ID]: id, For: p.product_title, Tier: p.properties['Custom Fit Tier'] || '' })) changed = true;
      }
      await syncNoteAttributes(cart, parents);
    } catch (e) { console.warn('[CustomFit] cart sync failed', e); }
    syncing = false;
    if (changed || forceRefresh) refreshCart();
    if (changed && /\/cart\/?$/.test(location.pathname)) location.reload();
  }

  if (/\/cart\/?$/.test(location.pathname)) syncCart();
})();